import appSettings from "@/appSettings";

const MS_PER_MINUTE = 60 * 1000;
const IDLE_TIMEOUT_MS = appSettings.idleTimeoutMinutes * MS_PER_MINUTE;
const WRITE_THROTTLE_MS = 5000;
const CHECK_INTERVAL_MS = 5000;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "wheel", "touchstart"];

export const LAST_ACTIVITY_KEY = "dv.exchange.idle.lastActivity";
export const SIGNED_OUT_KEY = "dv.exchange.idle.signedOut";

export const isLoginCallback = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  return params.has("code") && params.has("state");
};

/**
 * An unclean close leaves the last-activity stamp behind; a fresh login (the Auth0 redirect callback) must not
 * inherit it, while a normal load or silent restore keeps it so a long-idle reopen still signs out.
 */
export const clearStaleActivityOnLogin = ({ search = window.location.search, storage = window.localStorage } = {}) => {
  if (!isLoginCallback(search)) return;
  try {
    storage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    // Storage unavailable: nothing was persisted to clear.
  }
};

const noop = () => {};

export default {
  running: false,
  expired: false,
  pendingActivity: null,
  lastWrite: 0,
  localLastActivity: 0,
  intervalId: null,
  listeners: [],
  storage: null,
  now: () => Date.now(),
  callbacks: { onSignOut: noop },

  /**
   * Starts watching for inactivity. `now`, `storage`, `target` and `document` exist so tests can fake them.
   * Callback: onSignOut({ remote }).
   */
  start({
    onSignOut = noop,
    now = () => Date.now(),
    storage = window.localStorage,
    target = window,
    document: doc = window.document,
  } = {}) {
    this.stop();
    this.callbacks = { onSignOut };
    this.now = now;
    this.storage = storage;
    this.running = true;
    this.expired = false;
    this.pendingActivity = null;
    this.lastWrite = 0;

    const stored = this.readLastActivity();
    this.localLastActivity = stored ?? 0;
    if (stored === null) this.writeLastActivity(this.now());

    const onActivity = () => this.recordActivity();
    const onCheck = () => this.check();
    const onVisibility = () => {
      if (doc.visibilityState !== "hidden") this.check();
    };
    const onStorage = event => this.handleStorage(event);

    ACTIVITY_EVENTS.forEach(name => this.listen(target, name, onActivity));
    this.listen(target, "focus", onCheck);
    this.listen(target, "storage", onStorage);
    this.listen(doc, "visibilitychange", onVisibility);

    this.intervalId = setInterval(onCheck, CHECK_INTERVAL_MS);
    this.check();
  },

  stop() {
    if (this.intervalId !== null) clearInterval(this.intervalId);
    this.intervalId = null;
    this.listeners.forEach(({ target, name, handler, options }) => target.removeEventListener(name, handler, options));
    this.listeners = [];
    this.running = false;
    this.pendingActivity = null;
  },

  listen(target, name, handler) {
    const options = { capture: true, passive: true };
    target.addEventListener(name, handler, options);
    this.listeners.push({ target, name, handler, options });
  },

  /**
   * Counts as user activity. The shared timestamp is written at most every 5 seconds; an event inside that
   * window is kept and flushed by the next check, so the last movement is never lost. Input that arrives once
   * the deadline has passed expires instead of recording: a machine that slept with the tab visible fires no
   * wake event, so the overdue check may not have run yet and the stamp would grant a whole fresh period.
   */
  recordActivity() {
    if (!this.running || this.expired) return;
    const time = this.now();
    if (time >= this.effectiveLastActivity(time) + IDLE_TIMEOUT_MS) {
      this.expire(false);
      return;
    }
    if (time - this.lastWrite >= WRITE_THROTTLE_MS) {
      this.writeLastActivity(time);
      this.pendingActivity = null;
    } else {
      this.pendingActivity = time;
    }
  },

  signalSignedOut() {
    try {
      (this.storage ?? window.localStorage).setItem(SIGNED_OUT_KEY, String(this.now()));
    } catch {
      // Storage unavailable: the other tabs keep their own idle timers.
    }
  },

  readLastActivity() {
    try {
      const value = Number(this.storage.getItem(LAST_ACTIVITY_KEY));
      return Number.isFinite(value) && value > 0 ? value : null;
    } catch {
      return null;
    }
  },

  writeLastActivity(time) {
    this.lastWrite = time;
    this.localLastActivity = time;
    try {
      this.storage.setItem(LAST_ACTIVITY_KEY, String(time));
    } catch {
      // Storage rejected the write: the in-memory stamp keeps this tab counting down.
    }
  },

  /**
   * The newest of the shared stamp and this tab's own last write, never ahead of the clock, so neither a
   * storage failure nor a stamp another tab removed can push the deadline out of reach.
   */
  effectiveLastActivity(time) {
    return Math.min(Math.max(this.readLastActivity() ?? 0, this.localLastActivity), time);
  },

  check() {
    if (!this.running || this.expired) return;
    const time = this.now();

    if (this.pendingActivity !== null) {
      this.writeLastActivity(this.pendingActivity);
      this.pendingActivity = null;
    }

    if (time >= this.effectiveLastActivity(time) + IDLE_TIMEOUT_MS) this.expire(false);
  },

  expire(remote) {
    if (this.expired) return;
    this.expired = true;
    this.callbacks.onSignOut({ remote });
  },

  handleStorage(event) {
    if (event.key === LAST_ACTIVITY_KEY) {
      this.check();
    } else if (event.key === SIGNED_OUT_KEY && event.newValue) {
      this.expire(true);
    } else if (event.key === null) {
      // localStorage.clear() in another tab is not a logout on its own; the signal that follows one is.
      this.check();
    }
  },
};
